<template>
  <RevButton
    v-if="format === 'default'"
    :class="{
      '!bg-static-success-low !text-static-default-hi border-transparent !transition-none':
        atcSuccess,
    }"
    :disabled
    full-width="always"
    :icon="atcSuccess ? IconCheckInCircleFilled : IconPlus"
    icon-position="start"
    :loading="atcPending"
    variant="secondary"
    @click.stop.prevent="atcSuccess || atcPending ? null : addToCartHandler(id)"
  >
    <span class="body-1-bold">{{
      atcSuccess ? i18n(translations.ctaSuccessTitle) : text
    }}</span>
  </RevButton>
  <RevButtonIcon
    v-else
    :class="{
      '!bg-static-success-low !text-static-default-hi !transition-none':
        atcSuccess,
    }"
    :disabled
    :icon="atcSuccess ? IconCheckSmall : atcError ? IconWarning : IconPlus"
    :loading="atcPending"
    variant="primary"
    @click.stop.prevent="atcSuccess || atcPending ? null : addToCartHandler(id)"
  />
</template>

<script lang="ts" setup>
import { computed, inject, onMounted, ref } from 'vue'

import { useExperiments } from '@backmarket/nuxt-module-experiments/useExperiments'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { RevButton } from '@ds/components/Button'
import { RevButtonIcon } from '@ds/components/ButtonIcon'
import { IconCheckInCircleFilled } from '@ds/icons/IconCheckInCircleFilled'
import { IconCheckSmall } from '@ds/icons/IconCheckSmall'
import { IconPlus } from '@ds/icons/IconPlus'
import { IconWarning } from '@ds/icons/IconWarning'

import { useAddToCartPopover } from '@backmarket/nuxt-layer-recommendation/useAddToCartPopover'

import { useAddToCart } from '../../composables/useAddToCart'
import {
  type ProductTrackingData,
  useAddToCartTracking,
} from '../../composables/useProductTracking'
import {
  type ProductCardProduct,
  addToCartErrorModeKey,
  addToCartSuccessModeKey,
} from '../ProductCard/ProductCard.types'

import translations from './AddToCartButton.translations'

const {
  trackingData,
  product,
  format = 'default',
} = defineProps<{
  text?: string
  id: string
  trackingData: ProductTrackingData
  product: ProductCardProduct
  format?: 'default' | 'compact'
}>()

const successMode = inject(addToCartSuccessModeKey, 'popover')
const errorMode = inject(addToCartErrorModeKey, 'popover')

const disabled = ref(true)
onMounted(() => {
  disabled.value = false
})

const emit = defineEmits<{
  'add-to-cart': [{ status: string | undefined; error?: unknown }]
}>()

const { addToCart, addFlashSalesDiscountToCart, addingToCart } = useAddToCart()
const experiments = useExperiments()
const { show: showAddToCartPopover, showError: showAddToCartError } =
  useAddToCartPopover()
const i18n = useI18n()
const atcPending = computed(() => addingToCart.value === 'pending')
const atcSuccess = computed(() => addingToCart.value === 'success')
const atcError = computed(() => addingToCart.value === 'error')

async function addToCartHandler(id: string) {
  await addToCart(id)
  if (atcSuccess.value) {
    const { deal, listingPublicId } = product.rawProduct
    if (
      deal?.type === 'flash_sales' &&
      listingPublicId &&
      deal.discount_code &&
      experiments['experiment.flashSales'] === 'enabled' &&
      experiments['experiment.promoCodeInCart'] !== 'disabled'
    ) {
      addFlashSalesDiscountToCart(listingPublicId, deal.discount_code)
    }

    const { trackAddToCart } = useAddToCartTracking()
    trackAddToCart(trackingData)

    if (successMode === 'popover') {
      showAddToCartPopover(product)
    }
  } else if (atcError.value) {
    const blocking = errorMode === 'popover'
    showAddToCartError(blocking ? undefined : product, blocking)
  }
  emit('add-to-cart', { status: addingToCart.value })
}
</script>
